import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { BoutonPrincipal, BoutonSecondaire, MarqueObligatoire, MessageErreur } from '../ui/Primitives';
import { Modale } from '../ui/Modale';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import type { Profil } from '../../lib/types';
import {
  useCreerArticleFaq,
  useModifierArticleFaq,
  type ArticleFaq,
  type GestionFaq,
  type RubriqueFaq,
} from '../../lib/useFaq';

const CLASSE_LIBELLE = 'block text-[11px] font-bold uppercase tracking-wide text-codialis-navy';
const CLASSE_CHAMP =
  'mt-1 min-h-[44px] w-full min-w-0 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-codialis-navy';

function Champ({ id, libelle, aide, children }: { id: string; libelle: string; aide?: string; children: ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className={CLASSE_LIBELLE}>
        {libelle}
        <MarqueObligatoire />
      </label>
      {children}
      {aide && <p className="mt-1 text-[11px] text-codialis-gray">{aide}</p>}
    </div>
  );
}

/**
 * Création ou modification d'un article de la FAQ (TF-483), par la direction.
 * Les raccourcis « Apprenants », « Salariés » et « Tout le monde » cochent les
 * profils qu'ils désignent, servis par l'API ; seuls les profils partent.
 * Une création s'enregistre en brouillon ou se publie d'emblée ; une
 * modification garde le statut de l'article.
 */
export function FormulaireArticle({
  gestion,
  article,
  onFermer,
}: {
  gestion: GestionFaq;
  /** `null` : nouvel article. */
  article: ArticleFaq | null;
  onFermer: () => void;
}): JSX.Element {
  const id = useId();
  const toast = useToast();
  const creer = useCreerArticleFaq();
  const modifier = useModifierArticleFaq();
  const [saisie, setSaisie] = useState({
    rubrique: article?.rubrique ?? ('' as RubriqueFaq | ''),
    question: article?.question ?? '',
    reponse: article?.reponse ?? '',
    profils: article?.gestion?.profils ?? ([] as Profil[]),
  });
  const { limites } = gestion;
  const enCours = creer.isPending || modifier.isPending;
  const erreur = creer.error ?? modifier.error;

  const changer = <C extends keyof typeof saisie>(cle: C, valeur: (typeof saisie)[C]): void =>
    setSaisie((avant) => ({ ...avant, [cle]: valeur }));
  const basculerProfil = (profil: Profil): void =>
    changer('profils', saisie.profils.includes(profil) ? saisie.profils.filter((p) => p !== profil) : [...saisie.profils, profil]);
  const exactement = (profils: Profil[]): boolean =>
    profils.length === saisie.profils.length && profils.every((profil) => saisie.profils.includes(profil));

  const question = saisie.question.trim();
  const reponse = saisie.reponse.trim();
  const valide =
    saisie.rubrique !== '' &&
    question.length >= limites.question.min &&
    reponse.length >= limites.reponse.min &&
    saisie.profils.length > 0;

  const enregistrer = (publier: boolean): void => {
    if (!valide || saisie.rubrique === '') return;
    const corps = { rubrique: saisie.rubrique, question, reponse, profils: saisie.profils };
    if (article) {
      modifier.mutate(
        { id: article.id, ...corps },
        {
          onSuccess: () => {
            toast('Article enregistré');
            onFermer();
          },
        },
      );
      return;
    }
    creer.mutate(
      { ...corps, publier },
      {
        onSuccess: () => {
          toast(publier ? 'Article publié' : 'Brouillon enregistré : seule la direction le voit');
          onFermer();
        },
      },
    );
  };

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    enregistrer(false);
  };

  return (
    <Modale
      titre={article ? 'Modifier l’article' : 'Nouvel article'}
      sousTitre={
        article
          ? article.gestion?.statut === 'PUBLIE'
            ? 'Article publié : la modification est lue aussitôt.'
            : 'Brouillon : seule la direction le voit.'
          : 'Il naît en brouillon, sauf à le publier tout de suite.'
      }
      ouverte
      onFermer={onFermer}
    >
      <form onSubmit={envoyer} className="space-y-4 p-4 sm:p-6">
        <Champ id={`${id}-rubrique`} libelle="Rubrique">
          <select
            id={`${id}-rubrique`}
            value={saisie.rubrique}
            onChange={(evenement) => changer('rubrique', evenement.target.value as RubriqueFaq | '')}
            className={CLASSE_CHAMP}
          >
            <option value="">Choisir…</option>
            {gestion.rubriques.map((rubrique) => (
              <option key={rubrique.code} value={rubrique.code}>
                {rubrique.libelle}
              </option>
            ))}
          </select>
        </Champ>

        <Champ
          id={`${id}-question`}
          libelle="Question"
          aide={`${question.length} / ${limites.question.max} caractères`}
        >
          <input
            id={`${id}-question`}
            value={saisie.question}
            onChange={(evenement) => changer('question', evenement.target.value)}
            maxLength={limites.question.max}
            placeholder="Comment… ?"
            className={CLASSE_CHAMP}
          />
        </Champ>

        <Champ
          id={`${id}-reponse`}
          libelle="Réponse"
          aide={`Texte simple : une ligne vide sépare deux paragraphes · ${reponse.length} / ${limites.reponse.max} caractères`}
        >
          <textarea
            id={`${id}-reponse`}
            value={saisie.reponse}
            onChange={(evenement) => changer('reponse', evenement.target.value)}
            maxLength={limites.reponse.max}
            rows={9}
            className={`${CLASSE_CHAMP} resize-y`}
          />
        </Champ>

        <fieldset className="min-w-0">
          <legend className={CLASSE_LIBELLE}>
            Pour qui
            <MarqueObligatoire />
          </legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {gestion.publics.map((groupe) => (
              <button
                key={groupe.code}
                type="button"
                aria-pressed={exactement(groupe.profils)}
                onClick={() => changer('profils', groupe.profils)}
                className={`min-h-[36px] rounded-full border px-3 text-xs font-semibold transition ${
                  exactement(groupe.profils)
                    ? 'border-codialis-navy bg-codialis-navy text-white'
                    : 'border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
                }`}
              >
                {groupe.libelle}
              </button>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {gestion.profils.map((profil) => (
              <label
                key={profil.code}
                className="flex min-h-[40px] items-center gap-2 rounded-xl border border-gray-100 px-3 py-2 text-sm text-codialis-navy"
              >
                <input
                  type="checkbox"
                  checked={saisie.profils.includes(profil.code)}
                  onChange={() => basculerProfil(profil.code)}
                  className="h-4 w-4 shrink-0 accent-codialis-navy"
                />
                <span className="min-w-0">{profil.libelle}</span>
              </label>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-codialis-gray">La direction lit toute la FAQ, brouillons compris.</p>
        </fieldset>

        {erreur && (
          <MessageErreur message={erreur instanceof ErreurApi ? erreur.message : 'L’article n’a pas pu être enregistré.'} />
        )}

        <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          {article ? (
            <BoutonPrincipal type="submit" disabled={!valide || enCours}>
              {enCours ? 'Enregistrement…' : 'Enregistrer'}
            </BoutonPrincipal>
          ) : (
            <>
              <BoutonSecondaire type="submit" disabled={!valide || enCours}>
                Enregistrer le brouillon
              </BoutonSecondaire>
              <BoutonPrincipal onClick={() => enregistrer(true)} disabled={!valide || enCours}>
                {enCours ? 'Enregistrement…' : 'Publier'}
              </BoutonPrincipal>
            </>
          )}
        </div>
      </form>
    </Modale>
  );
}
